export type ModeId = 'flight' | 'fall' | 'swipe' | 'crossing';
export type WorldId = 'wonderland' | 'looking-glass' | 'dracula' | 'frankenstein' | 'oz' | 'pinocchio';

export interface World {
  id: WorldId;
  title: string;
  work: string;
  author: string;
  year: number;
  tagline: string;
  palette: [string, string, string];
  symbol: string;
  hazards: string[];
  safeObjects: string[];
}

export interface Character {
  id: string;
  name: string;
  worldId: WorldId;
  epithet: string;
  skin: string;
  hair: string;
  coat: string;
  accent: string;
  unlockCost: number;
  starter: boolean;
}

export interface Challenge {
  id: string;
  worldId: WorldId;
  mode: ModeId;
  title: string;
  chapter: string;
  chapterNumber: 1 | 2 | 3;
  objective: string;
  target: number;
  reward: number;
  /** Minimum time spent in the chapter before its goal can be claimed. */
  duration: number;
  /** Page cost to open this chapter after completing its predecessor. */
  unlockCost: number;
}

export const WORLDS: World[] = [
  {
    id: 'wonderland', title: 'Wonderland', work: "Alice's Adventures in Wonderland", author: 'Lewis Carroll', year: 1865,
    tagline: 'A curious little detour through a very large day.', palette: ['#263452', '#8b678c', '#f2d18a'], symbol: '♧',
    hazards: ['teacups', 'card guards', 'crooked clocks'], safeObjects: ['golden pages', 'white roses', 'sugar stars'],
  },
  {
    id: 'looking-glass', title: 'Looking-Glass Land', work: 'Through the Looking-Glass', author: 'Lewis Carroll', year: 1871,
    tagline: 'Every step forward starts by looking the other way.', palette: ['#182c48', '#6393a0', '#e8d18b'], symbol: '◇',
    hazards: ['chess pieces', 'shifting squares', 'mirror shards'], safeObjects: ['silver pages', 'talking flowers', 'glass stars'],
  },
  {
    id: 'dracula', title: 'Transylvania', work: 'Dracula', author: 'Bram Stoker', year: 1897,
    tagline: 'Race the moonlit weather above the old stone road.', palette: ['#17243b', '#613e68', '#e8b969'], symbol: '☾',
    hazards: ['storm lanterns', 'iron gates', 'lost feathers'], safeObjects: ['silver pages', 'protective charms', 'dawn sparks'],
  },
  {
    id: 'frankenstein', title: 'The Arctic Passage', work: 'Frankenstein; or, The Modern Prometheus', author: 'Mary Shelley', year: 1818,
    tagline: 'A bright idea can make a very loud entrance.', palette: ['#172d45', '#557f91', '#e9cf91'], symbol: '✦',
    hazards: ['live coils', 'ice shelves', 'loose gears'], safeObjects: ['copper pages', 'warm lanterns', 'blue sparks'],
  },
  {
    id: 'oz', title: 'The Land of Oz', work: 'The Wonderful Wizard of Oz', author: 'L. Frank Baum', year: 1900,
    tagline: 'Follow the road, and keep an eye on the weather.', palette: ['#26454e', '#77a97d', '#f0cf73'], symbol: '✿',
    hazards: ['tornado debris', 'poppy clouds', 'rolling apples'], safeObjects: ['golden pages', 'road markers', 'pocket stars'],
  },
  {
    id: 'pinocchio', title: 'The Puppet Theater', work: 'The Adventures of Pinocchio', author: 'Carlo Collodi', year: 1883,
    tagline: 'The stage is set. The strings are not always helpful.', palette: ['#3b3151', '#b16d5c', '#edcf85'], symbol: '♬',
    hazards: ['swinging strings', 'toy wheels', 'sea bubbles'], safeObjects: ['wooden pages', 'painted stars', 'little bells'],
  },
];

export const MODES: { id: ModeId; title: string; short: string; description: string; controls: string; glyph: string }[] = [
  { id: 'flight', title: 'Story Flight', short: 'Rise, dip, and find the gaps.', description: 'Tap to rise through moving storybook obstacles. Collect pages and see how many gates you can clear.', controls: 'Tap, click, or press Space to flap.', glyph: '↗' },
  { id: 'fall', title: 'Story Fall', short: 'Bounce your way up.', description: 'Steer between tiny platforms as the page scrolls higher. Landings are automatic; the steering is up to you.', controls: 'Hold the left or right side, or use A / D.', glyph: '⌁' },
  { id: 'swipe', title: 'Story Swipe', short: 'Sort the strange things in the air.', description: 'Swipe through helpful story objects and pages. Avoid hazards and build a combo before time runs out.', controls: 'Drag a finger or mouse across objects.', glyph: '✧' },
  { id: 'crossing', title: 'Story Crossing', short: 'Find a safe way through.', description: 'Step through moving lanes, pick up pages, and keep a clear route back to the story.', controls: 'Swipe or use arrows / WASD. The pad also works.', glyph: '⊞' },
];

export const CHARACTERS: Character[] = [
  { id: 'alice', name: 'Alice', worldId: 'wonderland', epithet: 'Curious explorer', skin: '#e8b999', hair: '#7d503c', coat: '#79a4a1', accent: '#f1d995', unlockCost: 0, starter: true },
  { id: 'white-rabbit', name: 'White Rabbit', worldId: 'wonderland', epithet: 'Keeper of the late bell', skin: '#e9d7c5', hair: '#eadfce', coat: '#bb7060', accent: '#e7c766', unlockCost: 55, starter: false },
  { id: 'red-queen', name: 'Red Queen', worldId: 'looking-glass', epithet: 'Ruler of the board', skin: '#e8c1a5', hair: '#5f3849', coat: '#a84f61', accent: '#e9cf89', unlockCost: 55, starter: false },
  { id: 'humpty', name: 'Humpty Dumpty', worldId: 'looking-glass', epithet: 'The wall-side philosopher', skin: '#e8d7b3', hair: '#725441', coat: '#668393', accent: '#d6a763', unlockCost: 0, starter: true },
  { id: 'dracula', name: 'Dracula', worldId: 'dracula', epithet: 'Night-flight enthusiast', skin: '#d5b39d', hair: '#2a2638', coat: '#4b385c', accent: '#b56a74', unlockCost: 0, starter: true },
  { id: 'mina', name: 'Mina Harker', worldId: 'dracula', epithet: 'Brave note-keeper', skin: '#e2bb9f', hair: '#503a39', coat: '#668284', accent: '#e7cf9b', unlockCost: 60, starter: false },
  { id: 'victor', name: 'Victor Frankenstein', worldId: 'frankenstein', epithet: 'Inventor of bright ideas', skin: '#d5b597', hair: '#51444b', coat: '#667a89', accent: '#dfbc69', unlockCost: 0, starter: true },
  { id: 'the-creature', name: 'The Creature', worldId: 'frankenstein', epithet: 'Thoughtful mountain walker', skin: '#a8b99b', hair: '#353f43', coat: '#536c65', accent: '#e5bc68', unlockCost: 65, starter: false },
  { id: 'dorothy', name: 'Dorothy', worldId: 'oz', epithet: 'Road-trip navigator', skin: '#e9b99d', hair: '#6d4b37', coat: '#668cb5', accent: '#d6b87a', unlockCost: 0, starter: true },
  { id: 'tin-woodman', name: 'Tin Woodman', worldId: 'oz', epithet: 'Heart of the team', skin: '#adb9ba', hair: '#697b82', coat: '#7b8b8c', accent: '#e2b75f', unlockCost: 65, starter: false },
  { id: 'pinocchio', name: 'Pinocchio', worldId: 'pinocchio', epithet: 'A puppet with a plan', skin: '#d7a878', hair: '#66452f', coat: '#a95f50', accent: '#e4c86e', unlockCost: 0, starter: true },
  { id: 'turquoise-fairy', name: 'Fairy with Turquoise Hair', worldId: 'pinocchio', epithet: 'A steady hand in a storm', skin: '#edc8a5', hair: '#54b8b1', coat: '#6785a4', accent: '#efd481', unlockCost: 70, starter: false },
];

const MODE_CONTENT: Record<ModeId, { title: string; objective: string; target: number; reward: number }[]> = {
  flight: [
    { title: 'The Unhurried Flight', objective: 'Clear the story gates', target: 7, reward: 14 },
    { title: 'A Page on the Wind', objective: 'Clear gates and gather pages', target: 8, reward: 16 },
    { title: 'The Night Road', objective: 'Outlast the changing sky', target: 7, reward: 15 },
    { title: 'A Spark Between Storms', objective: 'Pass through the bright machinery', target: 8, reward: 16 },
    { title: 'The Yellow Skyway', objective: 'Fly beyond the poppy clouds', target: 7, reward: 15 },
    { title: 'Curtain Call in the Air', objective: 'Thread the puppet stage', target: 8, reward: 16 },
  ],
  fall: [
    { title: 'Down the Rabbit Hole', objective: 'Reach the next illustrated shelf', target: 560, reward: 15 },
    { title: 'The Board Turns', objective: 'Climb across a shifting pattern', target: 600, reward: 16 },
    { title: 'Tower of Quiet Steps', objective: 'Climb above the storm line', target: 580, reward: 16 },
    { title: 'A Laboratory Shortcut', objective: 'Rise past the loose machinery', target: 620, reward: 17 },
    { title: 'Clouds Over Oz', objective: 'Find the road above the clouds', target: 600, reward: 16 },
    { title: 'The High Puppet Loft', objective: 'Bounce to the upper balcony', target: 580, reward: 16 },
  ],
  swipe: [
    { title: 'Tea-Time Tidy-Up', objective: 'Swipe helpful objects; avoid hazards', target: 12, reward: 14 },
    { title: 'Mirror Match', objective: 'Find the right reflections', target: 12, reward: 15 },
    { title: 'Before the First Light', objective: 'Clear the safe charms', target: 12, reward: 15 },
    { title: 'Sparks in the Workshop', objective: 'Sort sparks from loose parts', target: 12, reward: 15 },
    { title: 'Weather Watch', objective: 'Clear the path through the gusts', target: 12, reward: 15 },
    { title: 'The Stagehand Shift', objective: 'Cut the right strings and toys', target: 12, reward: 15 },
  ],
  crossing: [
    { title: 'The Queen’s Garden Path', objective: 'Reach the far side safely', target: 14, reward: 15 },
    { title: 'Across the Chessboard', objective: 'Cross a board that never sits still', target: 14, reward: 15 },
    { title: 'Before Sunrise', objective: 'Make it across the city lanes', target: 14, reward: 16 },
    { title: 'The Unsteady Laboratory', objective: 'Cross the humming floor', target: 14, reward: 16 },
    { title: 'Follow the Road', objective: 'Navigate the moving yellow path', target: 14, reward: 15 },
    { title: 'Between the Curtains', objective: 'Find a safe way through the stage', target: 14, reward: 15 },
  ],
};

const CHAPTER_ARC = [
  { number: 1 as const, suffix: '', duration: 75, unlockCost: 0, reward: 20 },
  { number: 2 as const, suffix: ': The Long Passage', duration: 120, unlockCost: 30, reward: 32 },
  { number: 3 as const, suffix: ': The Last Page', duration: 180, unlockCost: 60, reward: 50 },
];

function chapterTarget(mode: ModeId, baseTarget: number, chapterNumber: 1 | 2 | 3): number {
  const step = chapterNumber - 1;
  if (mode === 'flight') return baseTarget + step * 8;
  if (mode === 'fall') return baseTarget + step * 800;
  if (mode === 'swipe') return 30 + step * 25;
  return 30 + step * 30;
}

export const CHALLENGES: Challenge[] = WORLDS.flatMap((world, wi) => MODES.flatMap((mode) => {
  const base = MODE_CONTENT[mode.id][wi];
  return CHAPTER_ARC.map((arc) => ({
    id: arc.number === 1 ? `${world.id}-${mode.id}` : `${world.id}-${mode.id}-${arc.number}`,
    worldId: world.id,
    mode: mode.id,
    title: `${base.title}${arc.suffix}`,
    chapter: `${world.title} · ${mode.title} · Chapter ${['I', 'II', 'III'][arc.number - 1]}`,
    chapterNumber: arc.number,
    objective: base.objective,
    target: chapterTarget(mode.id, base.target, arc.number),
    reward: arc.reward + Math.max(0, base.reward - 14),
    duration: arc.duration,
    unlockCost: arc.unlockCost,
  }));
}));

export const worldById = (id: string): World => WORLDS.find((world) => world.id === id) ?? WORLDS[0];
export const modeById = (id: ModeId): (typeof MODES)[number] => MODES.find((mode) => mode.id === id) ?? MODES[0];
export const challengeById = (id: string): Challenge | undefined => CHALLENGES.find((challenge) => challenge.id === id);
export const charactersForWorld = (id: WorldId): Character[] => CHARACTERS.filter((character) => character.worldId === id);

export function dailyChallenge(date = new Date()): Challenge {
  const day = Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000);
  const openChapters = CHALLENGES.filter((challenge) => challenge.chapterNumber === 1);
  return openChapters[(day * 17) % openChapters.length];
}
